/**
 * web/src/shell/panels.tsx — the chat surface (#chat-screen): the invite
 * cards, the solo sealed stack, and the one panel that mounts for the active
 * tab / overlay (Settings, Help, Vault, Search, AI, Notebook, Us, Life,
 * Thread), moved verbatim out of App.tsx (card WUHnArc6lNID).
 */
import { useState, type Dispatch, type SetStateAction } from 'react';
import { InviteCard } from '../components/InviteCard';
import { InboxAddress } from '../components/InboxAddress';
import { SoloSealedStack } from '../components/SoloSealedStack';
import { FirstAct } from '../components/FirstAct';
import type { ComposeTile } from '../components/ComposeMenu';
/* Error visibility (card bdsxKQG9HrOQ): each panel gets its own boundary so a
   crash in one screen keeps the header and the bottom nav alive. */
import { PanelBoundary } from '../ui/ErrorBoundary';
import { Settings } from '../screens/Settings';
import { Help } from '../screens/Help';
import { Search } from '../screens/Search';
import { AIPanel } from '../screens/AI';
import { NotebookPanel } from '../screens/Notebook';
import { UsPanel } from '../screens/Us';
import type { GameId } from '../games/catalogue';
import { LifePanel } from '../screens/Life';
import { Thread } from '../screens/Thread';
/* Card 9QADJv3CdiOh — resolve a member's picked reply colour key to its hex
   (shared list, measured AA on the mustard partner bubble). */
import { replyColourHex } from '../../../shared/types';
import type { useSession } from '../hooks/useSession';
import type { useThread } from '../hooks/useThread';
import type { PearShare } from '../lib/shareTarget';
import { notebookIntent, type ComposeIntent, type Dest, type Tab } from './dest';

interface ChatSectionProps {
  onChat: boolean;
  tab: Tab;
  extraView: 'help' | null;
  setExtraView: Dispatch<SetStateAction<'help' | null>>;
  settingsOpen: boolean;
  showingAI: boolean;
  mountPanel: (el: HTMLDivElement | null) => void;
  session: ReturnType<typeof useSession>;
  thread: ReturnType<typeof useThread>;
  token: string | null;
  settingsTab: string;
  setSettingsTab: Dispatch<SetStateAction<string>>;
  setSettingsOpen: Dispatch<SetStateAction<boolean>>;
  leaveCouple: () => Promise<string | null>;
  signOut: () => Promise<void>;
  onOpenAI: () => void;
  onOpenHelp: () => void;
  onOpenTour: () => void;
  threadFilter: 'all' | 'chat' | null;
  notebookCollection: string | null;
  /** Card 5wr2rXdlCSTh: the item a Search result asked for, opened by
   *  Notebook and cleared once consumed. */
  notebookItem: string | null;
  setNotebookItem: Dispatch<SetStateAction<string | null>>;
  composeIntent: ComposeIntent;
  pendingShare: PearShare | null;
  consumePendingShare: () => void;
  openDatesFromNotebook: () => void;
  openUsFromNotebook: () => void;
  usTab: string;
  setUsTab: Dispatch<SetStateAction<string>>;
  usPrefsSignal: number;
  clearPrefsSignal: () => void;
  addDateIdeaInNotebook: () => void;
  lifeTab: string;
  setLifeTab: Dispatch<SetStateAction<string>>;
  handleComposePick: (tile: ComposeTile) => void;
  clearComposeIntent: () => void;
  navigateTo: (dest: Dest) => void;
  firstActOpen: boolean;
  dismissFirstAct: () => void;
  goFirstAct: (dest: Dest, intent?: ComposeIntent) => void;
  setTourOpen: Dispatch<SetStateAction<boolean>>;
}

export function ChatSection(props: ChatSectionProps) {
  const {
    onChat,
    tab,
    extraView,
    setExtraView,
    settingsOpen,
    showingAI,
    mountPanel,
    session,
    thread,
    token,
    settingsTab,
    setSettingsTab,
    setSettingsOpen,
    leaveCouple,
    signOut,
    onOpenAI,
    onOpenHelp,
    onOpenTour,
    threadFilter,
    notebookCollection,
    notebookItem,
    setNotebookItem,
    composeIntent,
    pendingShare,
    consumePendingShare,
    openDatesFromNotebook,
    openUsFromNotebook,
    usTab,
    setUsTab,
    usPrefsSignal,
    clearPrefsSignal,
    addDateIdeaInNotebook,
    lifeTab,
    setLifeTab,
    handleComposePick,
    clearComposeIntent,
    navigateTo,
    firstActOpen,
    dismissFirstAct,
    goFirstAct,
    setTourOpen,
  } = props;
  /* Card jT0odGfG7MVo: a game card in the thread still lands on the game it
     names — the Games tab now opens on a list, so the destination carries which
     tile to open, and `n` re-fires the intent when the same game is picked again. */
  const [usGamePick, setUsGamePick] = useState<{ id: GameId; n: number } | null>(null);
  return (
    <>
    {onChat && (
      <section
        className={`pear-chat fade-in`}
        id="chat-screen"
        /* The Thread has no panel wrapper of its own — it renders straight
           into this section — so the section IS its panel, but only while
           the Thread tab is the one up. Settings and the extra views take
           the whole surface over, and a tabpanel can only describe what is
           actually showing (UX item 6, H8). */
        role={
          tab === 'thread' && !extraView && !settingsOpen && !showingAI
            ? 'tabpanel'
            : undefined
        }
        aria-labelledby={
          tab === 'thread' && !extraView && !settingsOpen && !showingAI
            ? 'tab-thread'
            : undefined
        }
      >
        {/* TabBar renders as a bottom bar — see App.tsx bottom nav. */}
        {/* The code used to disappear with the pair screen, leaving a
            creator with nothing to send their partner. */}
        {/* The invite, as one card (cards AnqHIFLf6a5y + the Pair hero):
            the creator lands here, not on the Pair screen, so the share
            bar is where the invite actually has to leave the app —
            Copy / Share / Email / QR all read the same invite URL. */}
        {/* The invite blocks belong to the Thread, where the creator
            lands (Jonathan 2026-10-23: "on mobile there are excess
            lines/breaks at the top of the page" — on a solo couple they
            rode above EVERY tab: 468px of pair code + inbox address
            before Life/Notebook/Settings could start). Same condition
            the section uses to call itself the thread's tabpanel. */}
        {tab === 'thread' && !extraView && !settingsOpen && session.pendingCode && session.members.length < 2 && (
          <InviteCard
            code={session.pendingCode}
            mailEnabled={
              (session.authConfig?.verifyEmail ?? false) ||
              (session.authConfig?.forgotPassword ?? false)
            }
            idPrefix="chat-share-qr"
            variant="chat"
          />
        )}

        {/* The couple's shared inbox address at the invite moment (card
            qntf9Qa0Cemr, folded from 8ILRL6mrpBta) — the code's owner
            learns where to email before the partner even joins. Same
            server-issued code the Admin menu and Settings show. */}
        {tab === 'thread' && !extraView && !settingsOpen && session.pendingCode && session.members.length < 2 && (
          <InboxAddress
            code={session.pendingCode}
            idPrefix="invite"
            variant="inline"
            note="Once you're paired, email bills, bookings and receipts here — the concierge sorts each one into your shared space."
          />
        )}

        {/* Solo accumulation (card Pn1wrppZMWEr): the stack of sealed
            daily-question answers with its count — the solo home's proof
            that waiting is accumulating, not absence. It unmounts itself
            once paired, except for the unlock moment it holds open, and
            it rides the Thread tab only — a stack of cards above Life or
            Settings reads as spare line breaks at the top of the page
            (Jonathan 2026-10-23). */}
        {tab === 'thread' && !extraView && !settingsOpen && (
          <SoloSealedStack members={session.members} />
        )}

        {/* Panels mount only while their tab is active — the old shell
            kept all three in the DOM and lazily called mount() once.
            Settings replaces them while open; the tab bar stays so a tap
            on a tab is still a way out. */}
        {!extraView && settingsOpen && (
          <div id="panel-settings" className="pear-panel" ref={mountPanel}>
            <PanelBoundary>
              <Settings
                onClose={() => setSettingsOpen(false)}
                onLeave={leaveCouple}
                onSignOut={signOut}
                onOpenAI={onOpenAI}
                onOpenHelp={onOpenHelp}
                onOpenTour={onOpenTour}
                tab={settingsTab}
                onTabChange={setSettingsTab}
                pendingCode={session.pendingCode}
                awaitingPartner={session.members.length < 2}
                mailEnabled={
                  (session.authConfig?.verifyEmail ?? false) ||
                  (session.authConfig?.forgotPassword ?? false)
                }
              />
            </PanelBoundary>
          </div>
        )}

        {/* Full Help (card FiLhF6LRwAAP): the app's whole feature
            inventory in three beats, opened like the other extra views. */}
        {extraView === 'help' && (
          <div id="panel-help" className="pear-panel" ref={mountPanel}>
            <PanelBoundary>
              <Help
                onClose={() => setExtraView(null)}
                onTour={() => {
                  setExtraView(null);
                  setTourOpen(true);
                }}
              />
            </PanelBoundary>
          </div>
        )}

        {/* Vault is no longer a panel over the tabs: it is a tab inside
            Notebook (card 3YypYdChpTZD), so this overlay path is gone. */}

        {/* Search (card UW7Lu06zMJp9, promoted by 2Cium84y07TE): a real
            tab now, not a panel over the tabs. It keeps its own panel id
            so deep links and walks address the same element, and the
            gating matches the other tab panels — exactly one mounts. */}
        {!extraView && !settingsOpen && !showingAI && tab === 'search' && (
          <div id="panel-search" className="pear-panel" ref={mountPanel}>
            <PanelBoundary>
              <Search
                onClose={() => navigateTo({ section: 'notebook' })}
                onOpen={item => {
                  setNotebookItem(item.itemId);
                  navigateTo({ section: 'notebook' });
                }}
              />
            </PanelBoundary>
          </div>
        )}

        {/* AI panel — reachable from Settings > Concierge now that the AI tab is gone.
            It REPLACES the active tab panel rather than sitting beside it: with
            `tab` unchanged (Settings > Concierge never changes it) the old conditions
            rendered #panel-ai and #thread-scroll as two siblings in the flex
            column, so the AI screen stacked on top of the thread and — because
            Thread's chip effect runs once on mount — the invoke chip never came
            back after a permission was turned on. Gating every panel on
            !showingAI keeps exactly one mounted, and Thread remounts (and
            re-reads /api/ai/toggles) when the tab bar takes you back. */}
        {!extraView && !settingsOpen && showingAI && (
          <div id="panel-ai" className="pear-panel" ref={mountPanel}>
            <PanelBoundary>
              <AIPanel />
            </PanelBoundary>
          </div>
        )}
        {!extraView && !settingsOpen && !showingAI && tab === 'notebook' && (
          <div id="panel-notebook" className="pear-panel" ref={mountPanel} role="tabpanel" aria-labelledby="tab-notebook">
            <PanelBoundary>
              <NotebookPanel
                requestedCollection={notebookCollection}
                requestedItem={notebookItem}
                onItemConsumed={() => setNotebookItem(null)}
                intent={notebookIntent(composeIntent)}
                pendingShare={pendingShare}
                onShareConsumed={consumePendingShare}
                onOpenDates={openDatesFromNotebook}
                onOpenUs={openUsFromNotebook}
              />
            </PanelBoundary>
          </div>
        )}
        {!extraView && !settingsOpen && !showingAI && tab === 'us' && (
          <div id="panel-us" className={`pear-panel${usTab === 'questions' ? ' panel-questions-wide' : ''}${usTab === 'dates' ? ' panel-dates-wide' : ''}`} ref={mountPanel} role="tabpanel" aria-labelledby="tab-us">
            <PanelBoundary>
              <UsPanel
                members={session.members}
                intent={composeIntent?.kind === 'question' ? { kind: 'question' } : null}
                activeTab={usTab}
                onTabChange={setUsTab}
                prefsSignal={usPrefsSignal}
                gameIntent={usGamePick}
                onPrefsOpened={clearPrefsSignal}
                onAddDateIdea={addDateIdeaInNotebook}
              />
            </PanelBoundary>
          </div>
        )}
        {!extraView && !settingsOpen && !showingAI && tab === 'life' && (
          <div id="panel-life" className="pear-panel" ref={mountPanel} role="tabpanel" aria-labelledby="tab-life">
            <PanelBoundary>
              <LifePanel
                intent={composeIntent?.kind === 'ask' ? { kind: 'ask' } : null}
                activeTab={lifeTab}
                onTabChange={setLifeTab}
                members={session.members}
              />
            </PanelBoundary>
          </div>
        )}

        {!extraView && !settingsOpen && !showingAI && tab === 'thread' && (
          <>
            {/* The Thread has no panel wrapper of its own, so it gets the
                panel boundary directly — a composer crash must not take
                the header and the tab bar with it (card bdsxKQG9HrOQ). */}
            <PanelBoundary>
        {/* The first act (card Tw2G4BUmOF5o) — three choices at the top
            of the Thread instead of a nine-step modal over the app. Inline
            and dismissable, so the tabs, the composer and the invite card
            behind it stay live. */}
        {firstActOpen && (
          <FirstAct
            paired={session.members.length >= 2}
            onDismiss={dismissFirstAct}
            onGo={goFirstAct}
          />
        )}
        <Thread
            /* Paired vs solo (empty state): two members means the invite
               already landed, so the empty thread greets the couple
               instead of pointing back at the pairing screen. */
            paired={session.members.length >= 2}
            requestedFilter={threadFilter}
            messages={thread.messages}
            echoes={thread.echoes}
            /* Card 4FFux4skKvYw — sends the socket could not take are
               queued, not bounced: the row the user sees is the record,
               and Retry is the only path back to the network. */
            queued={thread.queued}
            onRetryQueued={thread.retryQueued}
            roleFor={thread.roleFor}
            nameFor={thread.nameFor}
            partnerName={thread.partnerName}
            /* The partner's picked reply ink (card 9QADJv3CdiOh), resolved
               off the same live members list as the name — a Settings
               change repaints the feed on the next members broadcast. */
            colourFor={(memberId) =>
              replyColourHex(session.members.find((m) => m.memberId === memberId)?.replyColour ?? null)}
            myMemberId={thread.myMemberId}
            onSend={thread.send}
            onRemoveMessage={thread.removeMessage}
            /* Card tPPB3ozS4YrK — the keystroke signal and its switch,
               both read from the same places as the header glow: the hook
               owns the socket (and the 2s window), the config owns whether
               the feature exists at all. */
            onTyping={thread.typing}
            typingOn={session.authConfig?.typing === true}
            /* Windowed hydration (card 4ynbWszGdqUd): hasOlder drives the
               "Show earlier messages" affordance, loadOlder pages back. */
            hasOlder={thread.hasOlder}
            onLoadOlder={thread.loadOlder}
            /* Archive (card 2sjo3uNvaRDr): find anything older than the
               window, then page history to the row that holds it. */
            onSearch={thread.search}
            onJumpToSeq={thread.loadToSeq}
            loading={Boolean(token) && !thread.historyLoaded}
            /* Card psHEfEkK9cT2 — the kudos baseline may only be taken once
               the history is really in hand. `loading` cannot carry that on a
               cold boot (it is false while the token is still null), so the
               celebration guard reads this explicit signal instead. */
            historyLoaded={Boolean(token) && thread.historyLoaded}
            onComposePick={handleComposePick}
            composeIntent={
              composeIntent?.kind === 'kudos'
                ? { kind: 'kudos' }
                : null
            }
            /* One-shot intent (card 4vIN4UouHPz4): the panel clears it the
               instant it acts, so returning to Thread can't resurrect the
               kudos sender from a stale intent. */
            onIntentConsumed={clearComposeIntent}
            /* Explicit destinations (card Mn55eOvFgvC0, H4): one
               navigateTo prop; each card names its own target subsection
               (Game round → Us>Game+roundId, question history →
               Us>Questions, locked date → Us>Dates, rail → Life>Chores). */
            onNavigate={navigateTo}
            onOpenGameRound={(roundId: string) => {
              setUsGamePick(p => ({ id: 'back-and-forth', n: (p?.n ?? 0) + 1 }));
              navigateTo({ section: 'us', subsection: 'game', entityId: roundId });
            }}
          />
            </PanelBoundary>
          </>
        )}
      </section>
    )}
    </>
  );
}
